import React from 'react';

// ── i18n strings ─────────────────────────────────────────────────────────────

type Lang = 'en' | 'fr' | 'it' | 'pl' | 'pt' | 'es' | 'ja';

interface PrivacyStrings {
    title: string;
    lastUpdated: string;
    sections: {
        collectTitle: string;
        collectItems: string[];
        useTitle: string;
        useItems: string[];
        adsTitle: string;
        adsItems: string[];
        thirdPartiesTitle: string;
        thirdPartiesItems: string[];
        retentionTitle: string;
        retentionItems: string[];
        rightsTitle: string;
        rightsItem1: string;
        rightsItem2: string; // "Or request deletion at: {url}" — {url} placeholder
        contactTitle: string;
        contactText: string; // "...Contact us at {email}" — {email} placeholder
    };
}

const strings: Record<Lang, PrivacyStrings> = {
    en: {
        title: 'Privacy Policy',
        lastUpdated: 'Last updated: August 2026',
        sections: {
            collectTitle: '1. What We Collect',
            collectItems: [
                'Precise location (GPS) — to paint tiles as you walk',
                'Name, email, profile photo — from Google / Apple Sign-In (only if you sign in)',
                'Push notification token — to notify you when your tiles are overtaken',
                'Gameplay data — your painted tiles, assigned paint color, coin balance and items you own',
                'Purchases — a record of in-app purchases you make (we never receive your card details)',
                'Content you post — map messages and forum posts, which are publicly visible to other players',
                'Advertising identifier — your device advertising ID (IDFA on iOS, Advertising ID on Android), used only for ads',
                'Diagnostics — crash reports and performance data',
            ],
            useTitle: '2. How We Use It',
            useItems: [
                'Render your position and painted tiles on the map',
                'Show other players who owns each tile',
                'Send overtake push notifications',
                'Process in-app purchases and deliver rewards',
                'Measure how the game is used so we can improve it, and diagnose crashes',
                'Show ads, which pay for running the game',
            ],
            adsTitle: '3. Advertising',
            adsItems: [
                'The game shows rewarded video ads. You choose when to watch one in exchange for coins — we never show ads you did not ask for.',
                'Ads are delivered through Unity LevelPlay (ironSource) mediation, which may source them from Unity Ads and other advertising networks.',
                'These partners may receive your device advertising identifier, approximate location, and in-app interaction data in order to select and measure ads. This counts as sharing data with third parties.',
                'On iOS 14 and later we ask your permission before using your advertising identifier for tracking. If you decline, you will still see ads, but they will be less relevant to you.',
                'You can reset or limit your advertising identifier at any time — iOS: Settings → Privacy & Security → Tracking. Android: Settings → Google → Ads.',
            ],
            thirdPartiesTitle: '4. Third Parties',
            thirdPartiesItems: [
                'Google Firebase — sign-in, database, push notifications, hosting, analytics, crash reporting and performance monitoring',
                'Unity LevelPlay / ironSource — ad mediation',
                'Unity Ads — advertising',
                'RevenueCat — in-app purchase management',
                'Mapbox — maps',
                'Expo — push notification delivery',
            ],
            retentionTitle: '5. Data Retention',
            retentionItems: [
                'Your data is retained while your account is active',
                'In-app account deletion is processed immediately',
                'Web deletion requests are processed within 7 days',
                'After deletion: tiles are released to the map (no longer linked to you); all other personal data is permanently deleted',
                'Data already shared with advertising partners is governed by their own privacy policies',
            ],
            rightsTitle: '6. Your Rights',
            rightsItem1: 'Delete your account: open the app → tap your avatar → Settings → Delete account',
            rightsItem2: 'Or request deletion at:',
            contactTitle: '7. Contact',
            contactText: 'Questions about your data? Contact us at',
        },
    },
    fr: {
        title: 'Politique de confidentialité',
        lastUpdated: 'Dernière mise à jour : août 2026',
        sections: {
            collectTitle: '1. Ce que nous collectons',
            collectItems: [
                'Localisation précise (GPS) — pour peindre des tuiles pendant vos déplacements',
                'Nom, e-mail, photo de profil — via Google / Apple Sign-In (uniquement si vous vous connectez)',
                'Jeton de notification push — pour vous avertir lorsque vos tuiles sont capturées',
                'Données de jeu — vos tuiles peintes, votre couleur attribuée, votre solde de pièces et vos objets',
                'Achats — un historique de vos achats intégrés (nous ne recevons jamais vos données bancaires)',
                'Contenu que vous publiez — messages sur la carte et publications du forum, visibles publiquement',
                "Identifiant publicitaire — l'identifiant publicitaire de votre appareil (IDFA sur iOS, Advertising ID sur Android), utilisé uniquement pour les publicités",
                'Diagnostics — rapports de plantage et données de performance',
            ],
            useTitle: '2. Comment nous les utilisons',
            useItems: [
                'Afficher votre position et vos tuiles peintes sur la carte',
                'Montrer aux autres joueurs qui possède chaque tuile',
                'Envoyer des notifications push de capture',
                'Traiter les achats intégrés et fournir les récompenses',
                'Mesurer comment le jeu est utilisé afin de l’améliorer, et diagnostiquer les plantages',
                'Afficher des publicités, qui financent le fonctionnement du jeu',
            ],
            adsTitle: '3. Publicité',
            adsItems: [
                'Le jeu affiche des vidéos publicitaires récompensées. Vous choisissez quand en regarder une en échange de pièces — nous n’affichons jamais de publicité que vous n’avez pas demandée.',
                'Les publicités sont diffusées via la médiation Unity LevelPlay (ironSource), qui peut les obtenir auprès d’Unity Ads et d’autres régies publicitaires.',
                'Ces partenaires peuvent recevoir l’identifiant publicitaire de votre appareil, votre localisation approximative et vos données d’interaction dans l’application afin de sélectionner et mesurer les publicités. Cela constitue un partage de données avec des tiers.',
                'Sur iOS 14 et versions ultérieures, nous demandons votre autorisation avant d’utiliser votre identifiant publicitaire à des fins de suivi. Si vous refusez, vous verrez toujours des publicités, mais elles seront moins pertinentes.',
                'Vous pouvez réinitialiser ou limiter votre identifiant publicitaire à tout moment — iOS : Réglages → Confidentialité et sécurité → Suivi. Android : Paramètres → Google → Annonces.',
            ],
            thirdPartiesTitle: '4. Tiers',
            thirdPartiesItems: [
                'Google Firebase — connexion, base de données, notifications push, hébergement, analytique, rapports de plantage et suivi des performances',
                'Unity LevelPlay / ironSource — médiation publicitaire',
                'Unity Ads — publicité',
                'RevenueCat — gestion des achats intégrés',
                'Mapbox — cartes',
                'Expo — envoi des notifications push',
            ],
            retentionTitle: '5. Conservation des données',
            retentionItems: [
                'Vos données sont conservées tant que votre compte est actif',
                "La suppression du compte depuis l'application est traitée immédiatement",
                'Les demandes de suppression via le web sont traitées sous 7 jours',
                "Après suppression : les tuiles sont libérées sur la carte (plus liées à vous) ; toutes les autres données personnelles sont définitivement supprimées",
                'Les données déjà partagées avec les partenaires publicitaires sont régies par leurs propres politiques de confidentialité',
            ],
            rightsTitle: '6. Vos droits',
            rightsItem1: "Supprimer votre compte : ouvrez l'application → touchez votre avatar → Paramètres → Supprimer le compte",
            rightsItem2: 'Ou demandez la suppression à :',
            contactTitle: '7. Contact',
            contactText: 'Des questions sur vos données ? Contactez-nous à',
        },
    },
    it: {
        title: 'Informativa sulla privacy',
        lastUpdated: 'Ultimo aggiornamento: agosto 2026',
        sections: {
            collectTitle: '1. Cosa raccogliamo',
            collectItems: [
                'Posizione precisa (GPS) — per dipingere tessere mentre cammini',
                'Nome, e-mail, foto profilo — tramite Google / Apple Sign-In (solo se accedi)',
                'Token di notifica push — per avvisarti quando le tue tessere vengono catturate',
                'Dati di gioco — le tue tessere dipinte, il colore assegnato, il saldo di monete e gli oggetti posseduti',
                'Acquisti — lo storico dei tuoi acquisti in-app (non riceviamo mai i dati della tua carta)',
                'Contenuti che pubblichi — messaggi sulla mappa e post del forum, visibili pubblicamente',
                "Identificatore pubblicitario — l'ID pubblicitario del tuo dispositivo (IDFA su iOS, Advertising ID su Android), usato solo per gli annunci",
                'Diagnostica — rapporti di arresto anomalo e dati sulle prestazioni',
            ],
            useTitle: '2. Come li utilizziamo',
            useItems: [
                'Mostrare la tua posizione e le tessere dipinte sulla mappa',
                'Mostrare agli altri giocatori chi possiede ogni tessera',
                'Inviare notifiche push di cattura',
                'Elaborare gli acquisti in-app e consegnare le ricompense',
                'Misurare come viene usato il gioco per migliorarlo e diagnosticare gli arresti anomali',
                'Mostrare annunci, che finanziano il funzionamento del gioco',
            ],
            adsTitle: '3. Pubblicità',
            adsItems: [
                'Il gioco mostra video pubblicitari con premio. Scegli tu quando guardarne uno in cambio di monete — non mostriamo mai annunci che non hai richiesto.',
                'Gli annunci vengono distribuiti tramite la mediazione Unity LevelPlay (ironSource), che può ottenerli da Unity Ads e da altri network pubblicitari.',
                "Questi partner possono ricevere l'identificatore pubblicitario del dispositivo, la posizione approssimativa e i dati di interazione nell'app per selezionare e misurare gli annunci. Ciò costituisce condivisione di dati con terze parti.",
                "Su iOS 14 e versioni successive chiediamo il tuo permesso prima di usare l'identificatore pubblicitario per il tracciamento. Se rifiuti, vedrai comunque annunci, ma saranno meno pertinenti.",
                "Puoi reimpostare o limitare il tuo identificatore pubblicitario in qualsiasi momento — iOS: Impostazioni → Privacy e sicurezza → Tracciamento. Android: Impostazioni → Google → Annunci.",
            ],
            thirdPartiesTitle: '4. Terze parti',
            thirdPartiesItems: [
                'Google Firebase — accesso, database, notifiche push, hosting, analisi, report di arresto anomalo e monitoraggio delle prestazioni',
                'Unity LevelPlay / ironSource — mediazione pubblicitaria',
                'Unity Ads — pubblicità',
                'RevenueCat — gestione degli acquisti in-app',
                'Mapbox — mappe',
                'Expo — invio delle notifiche push',
            ],
            retentionTitle: '5. Conservazione dei dati',
            retentionItems: [
                'I tuoi dati vengono conservati finché il tuo account è attivo',
                "L'eliminazione dell'account dall'app viene elaborata immediatamente",
                'Le richieste di eliminazione tramite web vengono elaborate entro 7 giorni',
                "Dopo l'eliminazione: le tessere vengono rilasciate sulla mappa (non più collegate a te); tutti gli altri dati personali vengono eliminati definitivamente",
                'I dati già condivisi con i partner pubblicitari sono regolati dalle loro informative sulla privacy',
            ],
            rightsTitle: '6. I tuoi diritti',
            rightsItem1: "Elimina il tuo account: apri l'app → tocca il tuo avatar → Impostazioni → Elimina account",
            rightsItem2: "Oppure richiedi l'eliminazione su:",
            contactTitle: '7. Contatti',
            contactText: 'Domande sui tuoi dati? Contattaci a',
        },
    },
    pl: {
        title: 'Polityka prywatności',
        lastUpdated: 'Ostatnia aktualizacja: sierpień 2026',
        sections: {
            collectTitle: '1. Co zbieramy',
            collectItems: [
                'Dokładna lokalizacja (GPS) — do malowania kafli podczas spaceru',
                'Imię, e-mail, zdjęcie profilowe — z Google / Apple Sign-In (tylko jeśli się logujesz)',
                'Token powiadomień push — aby powiadamiać Cię o przechwyceniu Twoich kafli',
                'Dane rozgrywki — Twoje pomalowane kafelki, przypisany kolor, stan monet i posiadane przedmioty',
                'Zakupy — historia zakupów w aplikacji (nigdy nie otrzymujemy danych Twojej karty)',
                'Publikowane treści — wiadomości na mapie i posty na forum, widoczne publicznie',
                'Identyfikator reklamowy — identyfikator reklamowy Twojego urządzenia (IDFA w iOS, Advertising ID w Android), używany wyłącznie do reklam',
                'Diagnostyka — raporty o awariach i dane o wydajności',
            ],
            useTitle: '2. Jak je wykorzystujemy',
            useItems: [
                'Wyświetlanie Twojej pozycji i pomalowanych kafli na mapie',
                'Pokazywanie innym graczom, kto jest właścicielem każdego kafla',
                'Wysyłanie powiadomień push o przejęciu kafla',
                'Realizacja zakupów w aplikacji i przyznawanie nagród',
                'Mierzenie sposobu korzystania z gry w celu jej ulepszania oraz diagnozowanie awarii',
                'Wyświetlanie reklam, które finansują działanie gry',
            ],
            adsTitle: '3. Reklamy',
            adsItems: [
                'Gra wyświetla reklamy wideo z nagrodą. Sam decydujesz, kiedy obejrzeć reklamę w zamian za monety — nigdy nie pokazujemy reklam, o które nie poprosiłeś.',
                'Reklamy są dostarczane przez mediację Unity LevelPlay (ironSource), która może pozyskiwać je z Unity Ads i innych sieci reklamowych.',
                'Ci partnerzy mogą otrzymywać identyfikator reklamowy Twojego urządzenia, przybliżoną lokalizację oraz dane o interakcjach w aplikacji, aby dobierać i mierzyć reklamy. Stanowi to udostępnianie danych podmiotom trzecim.',
                'W systemie iOS 14 i nowszym prosimy o zgodę przed użyciem identyfikatora reklamowego do śledzenia. Jeśli odmówisz, nadal będziesz widzieć reklamy, ale będą mniej dopasowane.',
                'W każdej chwili możesz zresetować lub ograniczyć identyfikator reklamowy — iOS: Ustawienia → Prywatność i bezpieczeństwo → Śledzenie. Android: Ustawienia → Google → Reklamy.',
            ],
            thirdPartiesTitle: '4. Podmioty trzecie',
            thirdPartiesItems: [
                'Google Firebase — logowanie, baza danych, powiadomienia push, hosting, analityka, raporty o awariach i monitorowanie wydajności',
                'Unity LevelPlay / ironSource — mediacja reklamowa',
                'Unity Ads — reklamy',
                'RevenueCat — obsługa zakupów w aplikacji',
                'Mapbox — mapy',
                'Expo — dostarczanie powiadomień push',
            ],
            retentionTitle: '5. Przechowywanie danych',
            retentionItems: [
                'Twoje dane są przechowywane, dopóki Twoje konto jest aktywne',
                'Usunięcie konta z poziomu aplikacji jest przetwarzane natychmiast',
                'Żądania usunięcia przez sieć są przetwarzane w ciągu 7 dni',
                'Po usunięciu: kafelki są zwalniane na mapę (niezwiązane z Tobą); wszystkie inne dane osobowe są trwale usuwane',
                'Dane już udostępnione partnerom reklamowym podlegają ich własnym politykom prywatności',
            ],
            rightsTitle: '6. Twoje prawa',
            rightsItem1: 'Usuń konto: otwórz aplikację → dotknij swojego awatara → Ustawienia → Usuń konto',
            rightsItem2: 'Lub poproś o usunięcie na:',
            contactTitle: '7. Kontakt',
            contactText: 'Pytania dotyczące Twoich danych? Skontaktuj się z nami:',
        },
    },
    pt: {
        title: 'Política de Privacidade',
        lastUpdated: 'Última atualização: agosto de 2026',
        sections: {
            collectTitle: '1. O que recolhemos',
            collectItems: [
                'Localização precisa (GPS) — para pintar azulejos enquanto caminha',
                'Nome, e-mail, foto de perfil — via Google / Apple Sign-In (apenas se iniciar sessão)',
                'Token de notificação push — para notificá-lo quando os seus azulejos forem capturados',
                'Dados de jogo — os seus azulejos pintados, cor atribuída, saldo de moedas e itens que possui',
                'Compras — o registo das suas compras na aplicação (nunca recebemos os dados do seu cartão)',
                'Conteúdo que publica — mensagens no mapa e publicações no fórum, visíveis publicamente',
                'Identificador de publicidade — o ID de publicidade do seu dispositivo (IDFA no iOS, Advertising ID no Android), usado apenas para anúncios',
                'Diagnósticos — relatórios de falhas e dados de desempenho',
            ],
            useTitle: '2. Como os utilizamos',
            useItems: [
                'Mostrar a sua posição e azulejos pintados no mapa',
                'Mostrar a outros jogadores quem é proprietário de cada azulejo',
                'Enviar notificações push de captura',
                'Processar compras na aplicação e entregar recompensas',
                'Medir como o jogo é utilizado para o melhorarmos e diagnosticar falhas',
                'Mostrar anúncios, que financiam o funcionamento do jogo',
            ],
            adsTitle: '3. Publicidade',
            adsItems: [
                'O jogo mostra anúncios de vídeo com recompensa. É você que escolhe quando ver um em troca de moedas — nunca mostramos anúncios que não tenha pedido.',
                'Os anúncios são fornecidos através da mediação Unity LevelPlay (ironSource), que os pode obter da Unity Ads e de outras redes publicitárias.',
                'Estes parceiros podem receber o identificador de publicidade do seu dispositivo, a localização aproximada e os dados de interação na aplicação para selecionar e medir anúncios. Isto constitui partilha de dados com terceiros.',
                'No iOS 14 e posterior pedimos a sua autorização antes de usar o identificador de publicidade para efeitos de rastreio. Se recusar, continuará a ver anúncios, mas serão menos relevantes.',
                'Pode repor ou limitar o seu identificador de publicidade a qualquer momento — iOS: Definições → Privacidade e Segurança → Rastreio. Android: Definições → Google → Anúncios.',
            ],
            thirdPartiesTitle: '4. Terceiros',
            thirdPartiesItems: [
                'Google Firebase — início de sessão, base de dados, notificações push, alojamento, análises, relatórios de falhas e monitorização de desempenho',
                'Unity LevelPlay / ironSource — mediação publicitária',
                'Unity Ads — publicidade',
                'RevenueCat — gestão de compras na aplicação',
                'Mapbox — mapas',
                'Expo — envio de notificações push',
            ],
            retentionTitle: '5. Retenção de dados',
            retentionItems: [
                'Os seus dados são conservados enquanto a sua conta estiver ativa',
                'A eliminação de conta na aplicação é processada imediatamente',
                'Os pedidos de eliminação via web são processados em 7 dias',
                'Após eliminação: os azulejos são libertados para o mapa (não associados a si); todos os outros dados pessoais são permanentemente eliminados',
                'Os dados já partilhados com parceiros publicitários regem-se pelas políticas de privacidade desses parceiros',
            ],
            rightsTitle: '6. Os seus direitos',
            rightsItem1: 'Eliminar a sua conta: abra a aplicação → toque no seu avatar → Definições → Eliminar conta',
            rightsItem2: 'Ou solicite a eliminação em:',
            contactTitle: '7. Contacto',
            contactText: 'Dúvidas sobre os seus dados? Contacte-nos em',
        },
    },
    es: {
        title: 'Política de privacidad',
        lastUpdated: 'Última actualización: agosto de 2026',
        sections: {
            collectTitle: '1. Qué recopilamos',
            collectItems: [
                'Ubicación precisa (GPS) — para pintar casillas mientras caminas',
                'Nombre, correo electrónico, foto de perfil — desde Google / Apple Sign-In (solo si inicias sesión)',
                'Token de notificación push — para avisarte cuando te capturen una casilla',
                'Datos de juego — tus casillas pintadas, color asignado, saldo de monedas y objetos que posees',
                'Compras — el registro de tus compras dentro de la app (nunca recibimos los datos de tu tarjeta)',
                'Contenido que publicas — mensajes en el mapa y publicaciones del foro, visibles públicamente',
                'Identificador publicitario — el ID de publicidad de tu dispositivo (IDFA en iOS, Advertising ID en Android), usado solo para anuncios',
                'Diagnósticos — informes de fallos y datos de rendimiento',
            ],
            useTitle: '2. Cómo los usamos',
            useItems: [
                'Mostrar tu posición y casillas pintadas en el mapa',
                'Mostrar a otros jugadores quién es dueño de cada casilla',
                'Enviar notificaciones push de captura',
                'Procesar las compras dentro de la app y entregar recompensas',
                'Medir cómo se usa el juego para mejorarlo y diagnosticar fallos',
                'Mostrar anuncios, que financian el funcionamiento del juego',
            ],
            adsTitle: '3. Publicidad',
            adsItems: [
                'El juego muestra anuncios de vídeo recompensados. Tú eliges cuándo ver uno a cambio de monedas — nunca mostramos anuncios que no hayas pedido.',
                'Los anuncios se sirven mediante la mediación de Unity LevelPlay (ironSource), que puede obtenerlos de Unity Ads y de otras redes publicitarias.',
                'Estos socios pueden recibir el identificador publicitario de tu dispositivo, tu ubicación aproximada y datos de interacción en la app para seleccionar y medir anuncios. Esto constituye compartir datos con terceros.',
                'En iOS 14 y posteriores pedimos tu permiso antes de usar tu identificador publicitario con fines de seguimiento. Si lo rechazas, seguirás viendo anuncios, pero serán menos relevantes.',
                'Puedes restablecer o limitar tu identificador publicitario en cualquier momento — iOS: Ajustes → Privacidad y seguridad → Rastreo. Android: Ajustes → Google → Anuncios.',
            ],
            thirdPartiesTitle: '4. Terceros',
            thirdPartiesItems: [
                'Google Firebase — inicio de sesión, base de datos, notificaciones push, alojamiento, analíticas, informes de fallos y supervisión del rendimiento',
                'Unity LevelPlay / ironSource — mediación publicitaria',
                'Unity Ads — publicidad',
                'RevenueCat — gestión de compras dentro de la app',
                'Mapbox — mapas',
                'Expo — envío de notificaciones push',
            ],
            retentionTitle: '5. Retención de datos',
            retentionItems: [
                'Tus datos se conservan mientras tu cuenta esté activa',
                'La eliminación de cuenta desde la app se procesa de inmediato',
                'Las solicitudes de eliminación web se procesan en un plazo de 7 días',
                'Tras la eliminación: las casillas se liberan en el mapa (ya no vinculadas a ti); todos los demás datos personales se eliminan permanentemente',
                'Los datos ya compartidos con socios publicitarios se rigen por sus propias políticas de privacidad',
            ],
            rightsTitle: '6. Tus derechos',
            rightsItem1: 'Eliminar tu cuenta: abre la app → toca tu avatar → Ajustes → Eliminar cuenta',
            rightsItem2: 'O solicita la eliminación en:',
            contactTitle: '7. Contacto',
            contactText: '¿Dudas sobre tus datos? Contáctanos en',
        },
    },
    ja: {
        title: 'プライバシーポリシー',
        lastUpdated: '最終更新: 2026年8月',
        sections: {
            collectTitle: '1. 収集する情報',
            collectItems: [
                '正確な位置情報（GPS）— 移動に合わせてタイルを塗るために使用します',
                '名前、メールアドレス、プロフィール写真 — Google / Apple サインインから（サインインした場合のみ）',
                'プッシュ通知トークン — あなたのタイルが奪われた際に通知するために使用します',
                'ゲームデータ — 塗ったタイル、割り当てられた色、コイン残高、所有アイテム',
                '購入履歴 — アプリ内購入の記録（カード情報を受け取ることはありません）',
                '投稿したコンテンツ — マップ上のメッセージやフォーラムの投稿。他のプレイヤーに公開されます',
                '広告識別子 — 端末の広告 ID（iOS では IDFA、Android では Advertising ID）。広告のみに使用します',
                '診断情報 — クラッシュレポートおよびパフォーマンスデータ',
            ],
            useTitle: '2. 利用目的',
            useItems: [
                'マップ上にあなたの位置と塗ったタイルを表示するため',
                '各タイルの所有者を他のプレイヤーに表示するため',
                'タイルが奪われた際のプッシュ通知を送るため',
                'アプリ内購入の処理と報酬の付与のため',
                'ゲームの利用状況を測定して改善し、クラッシュを調査するため',
                'ゲームの運営費をまかなう広告を表示するため',
            ],
            adsTitle: '3. 広告について',
            adsItems: [
                '本ゲームではリワード動画広告を表示します。コインと引き換えに広告を見るかどうかは、あなたが選べます。求めていない広告を表示することはありません。',
                '広告は Unity LevelPlay（ironSource）のメディエーションを通じて配信され、Unity Ads やその他の広告ネットワークから提供される場合があります。',
                'これらのパートナーは、広告の選択と効果測定のために、端末の広告識別子、おおよその位置情報、アプリ内の操作データを受け取ることがあります。これは第三者へのデータ共有にあたります。',
                'iOS 14 以降では、広告識別子をトラッキングに使用する前に許可を求めます。許可しない場合でも広告は表示されますが、関連性は低くなります。',
                'デバイスの設定からいつでも広告識別子をリセットまたは制限できます — iOS: 設定 → プライバシーとセキュリティ → トラッキング。Android: 設定 → Google → 広告。',
            ],
            thirdPartiesTitle: '4. 第三者',
            thirdPartiesItems: [
                'Google Firebase — サインイン、データベース、プッシュ通知、ホスティング、分析、クラッシュレポート、パフォーマンス監視',
                'Unity LevelPlay / ironSource — 広告メディエーション',
                'Unity Ads — 広告配信',
                'RevenueCat — アプリ内購入の管理',
                'Mapbox — 地図',
                'Expo — プッシュ通知の配信',
            ],
            retentionTitle: '5. データの保持',
            retentionItems: [
                'アカウントが有効な間、データは保持されます',
                'アプリ内でのアカウント削除は直ちに処理されます',
                'ウェブ経由の削除リクエストは 7 日以内に処理されます',
                '削除後: タイルはマップ上に開放され（あなたとの紐付けは解除されます）、その他の個人データは完全に削除されます',
                'すでに広告パートナーと共有されたデータは、各社のプライバシーポリシーに従って取り扱われます',
            ],
            rightsTitle: '6. あなたの権利',
            rightsItem1: 'アカウントの削除: アプリを開く → アバターをタップ → 設定 → アカウントを削除',
            rightsItem2: 'または、次のページから削除をリクエストしてください:',
            contactTitle: '7. お問い合わせ',
            contactText: 'データに関するご質問はこちらまで',
        },
    },
};

// ── Language detection ────────────────────────────────────────────────────────

function detectLang(): Lang {
    const browserLang = (navigator.language || navigator.languages?.[0] || 'en').toLowerCase();
    if (browserLang.startsWith('fr')) return 'fr';
    if (browserLang.startsWith('it')) return 'it';
    if (browserLang.startsWith('pl')) return 'pl';
    if (browserLang.startsWith('pt')) return 'pt';
    if (browserLang.startsWith('es')) return 'es';
    if (browserLang.startsWith('ja')) return 'ja';
    return 'en';
}

// ── Component ─────────────────────────────────────────────────────────────────

const Privacy = () => {
    const contactEmail = process.env.REACT_APP_CONTACT_EMAIL || 'support@painttheworld.app';
    const deleteUrl = 'https://painttheworld.app/delete-account';
    const lang = detectLang();
    const t = strings[lang];
    const s = t.sections;

    return (
        <div style={styles.container}>
            <h1 style={styles.heading}>{t.title}</h1>
            <p style={styles.updated}>{t.lastUpdated}</p>

            <section style={styles.section}>
                <h2 style={styles.sectionHeading}>{s.collectTitle}</h2>
                <ul style={styles.list}>
                    {s.collectItems.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeading}>{s.useTitle}</h2>
                <ul style={styles.list}>
                    {s.useItems.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeading}>{s.adsTitle}</h2>
                <ul style={styles.list}>
                    {s.adsItems.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeading}>{s.thirdPartiesTitle}</h2>
                <ul style={styles.list}>
                    {s.thirdPartiesItems.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeading}>{s.retentionTitle}</h2>
                <ul style={styles.list}>
                    {s.retentionItems.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeading}>{s.rightsTitle}</h2>
                <ul style={styles.list}>
                    <li>{s.rightsItem1}</li>
                    <li>
                        {s.rightsItem2}{' '}
                        <a href={deleteUrl} style={styles.link}>{deleteUrl.replace('https://', '')}</a>
                    </li>
                </ul>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeading}>{s.contactTitle}</h2>
                <p style={styles.body}>
                    {s.contactText}{' '}
                    <a href={`mailto:${contactEmail}`} style={styles.link}>{contactEmail}</a>
                </p>
            </section>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    container: {
        maxWidth: 640,
        margin: '0 auto',
        padding: '24px 16px',
        fontFamily: 'system-ui, sans-serif',
        color: '#eee',
        backgroundColor: '#111',
        minHeight: '100vh',
    },
    heading: { color: '#fff', fontSize: 28, fontWeight: 700, marginBottom: 4 },
    updated: { color: '#666', fontSize: 13, marginBottom: 32 },
    section: { marginBottom: 28 },
    sectionHeading: { color: '#fff', fontSize: 18, fontWeight: 600, marginBottom: 12 },
    list: { color: '#eee', lineHeight: 1.7, paddingLeft: 20 },
    body: { color: '#eee', lineHeight: 1.7 },
    link: { color: '#6ab4ff' },
};

export default Privacy;
